import * as TabsPrimitive from "@radix-ui/react-tabs";
import clsx from "clsx";
import React from "react";
import { createPluggable } from "pluggable-react";
import styles from "./Tabs.module.scss";
import { DismissableProvider, useDismissable } from "../DismissGuard/DismissGuard";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { withRefProp } from "../../../utils/withRefProp";

type TabsIntent = "default" | "bare";

const TabsIntentContext = React.createContext<TabsIntent>("default");

interface TabsDismissRootProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  urlSync?: string;
  children: (props: Pick<TabsProps, "value" | "onValueChange">) => React.ReactNode;
}

const readUrlValue = (param: string) => {
  if (typeof window === "undefined") return undefined;
  return new URLSearchParams(window.location.search).get(param) ?? undefined;
};

const writeUrlValue = (param: string, value: string) => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set(param, value);
  window.history.replaceState(window.history.state, "", url);
};

const TabsDismissRoot = ({ value, defaultValue, onValueChange, urlSync, children }: TabsDismissRootProps) => {
  const { dismiss } = useDismissable();
  const [internalValue, setInternalValue] = React.useState(() => (urlSync ? readUrlValue(urlSync) : undefined) ?? defaultValue);
  const controlled = value !== undefined;
  const currentValue = controlled ? value : internalValue;

  React.useEffect(() => {
    if (!urlSync) return undefined;
    const onPopState = () => {
      const nextValue = readUrlValue(urlSync) ?? defaultValue;
      if (!controlled) setInternalValue(nextValue);
      if (nextValue) onValueChange?.(nextValue);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [controlled, defaultValue, onValueChange, urlSync]);

  const handleValueChange = React.useCallback((nextValue: string) => {
    if (nextValue === currentValue) return;

    dismiss(() => {
      if (!controlled) {
        setInternalValue(nextValue);
      }
      if (urlSync) {
        writeUrlValue(urlSync, nextValue);
      }
      onValueChange?.(nextValue);
    });
  }, [controlled, currentValue, dismiss, onValueChange, urlSync]);

  return children({
    value: currentValue,
    onValueChange: handleValueChange,
  });
};

export interface TabsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
  children: React.ReactNode;
  intent?: TabsIntent;
  urlSync?: string;
}

const DefaultTabs = (props: TabsProps) => {
  const { children, intent = "default", value, defaultValue, onValueChange, urlSync, ...otherProps } = props;
  return (
    <DismissableProvider>
      <TabsIntentContext.Provider value={intent}>
        <TabsDismissRoot value={value} defaultValue={defaultValue} onValueChange={onValueChange} urlSync={urlSync}>
          {(dismissableProps) => (
            <TabsPrimitive.Root {...otherProps} {...dismissableProps}>{children}</TabsPrimitive.Root>
          )}
        </TabsDismissRoot>
      </TabsIntentContext.Provider>
    </DismissableProvider>
  );
};

const Tabs = createPluggable<TabsProps>("deci.tabs", DefaultTabs);

export interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  barPlacement?: "top" | "bottom";
  ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.List>>;
}

const DefaultTabsList = withRefProp(({ className, barPlacement, ref, ...props }: TabsListProps) => {
  const intent = React.useContext(TabsIntentContext);
  return (
    <TabsPrimitive.List
      ref={ref}
      className={clsx(styles.list, className)}
      data-bar-placement={barPlacement ?? "bottom"}
      data-intent={intent}
      data-deci-tabs-list="true"
      {...props}
    />
  );
});

const TabsList = createPluggable<TabsListProps>("deci.tabsList", DefaultTabsList);

export interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.Trigger>>;
}

const DefaultTabsTrigger = withRefProp(({ className, ref, ...props }: TabsTriggerProps) => {
  const intent = React.useContext(TabsIntentContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={clsx(styles.trigger, className)}
      data-intent={intent}
      data-deci-tabs-trigger="true"
      {...props}
    />
  );
});

const TabsTrigger = createPluggable<TabsTriggerProps>("deci.tabsTrigger", DefaultTabsTrigger);

export interface TabsContentProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {
  ref?: React.Ref<React.ElementRef<typeof TabsPrimitive.Content>>;
  loadingFallback?: React.ReactNode;
}

const defaultLoadingFallback = (
  <div style={{ alignItems: "center", display: "flex", height: "100%", justifyContent: "center", width: "100%" }}>
    <LoadingSpinner />
  </div>
);

const DefaultTabsContent = withRefProp(({ className, ref, loadingFallback, children, ...props }: TabsContentProps) => (
  <TabsPrimitive.Content
    ref={ref}
    className={clsx(styles.content, className)}
    data-deci-tabs-content="true"
    {...props}
  >
    <React.Suspense fallback={loadingFallback ?? defaultLoadingFallback}>
      {children}
    </React.Suspense>
  </TabsPrimitive.Content>
));

const TabsContent = createPluggable<TabsContentProps>("deci.tabsContent", DefaultTabsContent);

export interface TabsIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  ref?: React.Ref<HTMLSpanElement>;
}

const DefaultTabsIcon = withRefProp(({ className, ref, ...props }: TabsIconProps) => (
  <span
    ref={ref}
    className={clsx(styles.icon, className)}
    aria-hidden="true"
    data-deci-tabs-icon="true"
    {...props}
  />
));

const TabsIcon = createPluggable<TabsIconProps>("deci.tabsIcon", DefaultTabsIcon);

export type TabValue = {
  value: string;
  label: string;
  icon?: React.ReactElement;
  content: React.ReactElement;
};

export default Object.assign(Tabs, {
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
  Icon: TabsIcon,
});
